import { api } from './api';

export type MfaPolicy = 'optional' | 'mandatory';
export type MfaMethod = 'totp' | 'backup' | 'email';

export interface MfaStatus {
  enabled: boolean;
  policy: MfaPolicy;
  required_by_policy: boolean;
  setup_required: boolean;
  backup_codes_remaining: number;
}

export interface MfaSetupInfo {
  provisioning_uri: string;
  secret: string;
}

export interface MfaLoginChallenge {
  mfa_required: true;
  setup_required: boolean;
  mfa_token: string;
}

export interface AuthTokens {
  access_token: string;
  expires_in: number;
  token_type?: string;
  backup_codes?: string[];
}

export function isMfaChallenge(data: unknown): data is MfaLoginChallenge {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as MfaLoginChallenge).mfa_required === true &&
    typeof (data as MfaLoginChallenge).mfa_token === 'string'
  );
}

class MfaService {
  async getStatus(): Promise<MfaStatus> {
    const { data } = await api.get<MfaStatus>('/v1/auth/mfa/status');
    return data;
  }

  async startSetup(): Promise<MfaSetupInfo> {
    const { data } = await api.post<MfaSetupInfo>('/v1/auth/mfa/setup');
    return data;
  }

  async startSetupLogin(mfaToken: string): Promise<MfaSetupInfo> {
    const { data } = await api.post<MfaSetupInfo>('/v1/auth/mfa/setup-login', {
      mfa_token: mfaToken,
    });
    return data;
  }

  async confirmSetup(code: string): Promise<{ message: string; backup_codes: string[] }> {
    const { data } = await api.post('/v1/auth/mfa/confirm', { code });
    return data;
  }

  async sendEmailCode(mfaToken: string): Promise<{ message: string }> {
    const { data } = await api.post('/v1/auth/mfa/send-email-code', { mfa_token: mfaToken });
    return data;
  }

  async completeLogin(
    mfaToken: string,
    code: string,
    method: MfaMethod = 'totp'
  ): Promise<AuthTokens> {
    const { data } = await api.post<AuthTokens>('/v1/auth/mfa/complete', {
      mfa_token: mfaToken,
      code,
      method,
    });
    return data;
  }

  async regenerateBackupCodes(
    password: string,
    code: string
  ): Promise<{ backup_codes: string[]; remaining: number }> {
    const { data } = await api.post('/v1/auth/mfa/backup-codes/regenerate', { password, code });
    return data;
  }

  async disable(password: string, code: string): Promise<{ message: string }> {
    const { data } = await api.post('/v1/auth/mfa/disable', { password, code });
    return data;
  }

  async getPlatformPolicy(): Promise<{ policy: MfaPolicy }> {
    const { data } = await api.get('/v1/admin/security/mfa-policy');
    return data;
  }

  async updatePlatformPolicy(policy: MfaPolicy): Promise<{ policy: MfaPolicy }> {
    const { data } = await api.patch('/v1/admin/security/mfa-policy', { policy });
    return data;
  }
}

export const mfaService = new MfaService();
